import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';

import Header from '../components/Header';
import FilterStrip from '../components/FilterStrip';
import DashboardSummary from '../components/DashboardSummary';
import TabStockAvailability from '../components/tabs/TabStockAvailability';
import TabNearExpiry from '../components/tabs/TabNearExpiry';
import TabStockout from '../components/tabs/TabStockout';
import TabExpired from '../components/tabs/TabExpired';
import TabPipeline from '../components/tabs/TabPipeline';
import Footer from '../components/Footer';
import { Landmark, Hospital, Pill, BriefcaseMedical } from 'lucide-react';
import Procurement from '../components/tabs/Procurement';
import { fetchDashboardSummary, fetchHmisStockStats } from '../data/api';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler
);

const tabs = [
  { id: 'tab1', label: 'Stock Availability', cls: 'vtab-1' },
  { id: 'tab2', label: 'Near Expiry', cls: 'vtab-2', badge: '50' },
  { id: 'tab3', label: 'Stockout', cls: 'vtab-3', badge: '203' },
  { id: 'tab4', label: 'Expired', cls: 'vtab-4' },
  { id: 'tab5', label: 'Pipeline', cls: 'vtab-5' },
  { id: 'tab6', label: 'Procurement', cls: 'vtab-6' },
];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('tab1');
  const [summary, setSummary] = useState(null);
  const [stockStats, setStockStats] = useState(null);

  useEffect(() => {
    fetchDashboardSummary().then(r => setSummary(r.data)).catch(console.error);
    fetchHmisStockStats().then(r => setStockStats(r.data)).catch(console.error);
  }, []);

  const summaryData = summary && stockStats ? [
    {
      title: "Stock Availability",
      value: `${stockStats.overall_availability}%`,
      subtitle: `${stockStats.total_stores} stores monitored`,
      progress: parseFloat(stockStats.overall_availability),
      trend: 3, color: "blue"
    },
    {
      title: "Facilities \u2265 90%",
      value: `${((stockStats.above_90 / stockStats.total_stores) * 100).toFixed(0)}%`,
      subtitle: `${stockStats.above_90} stores at target`,
      progress: Math.round((stockStats.above_90 / stockStats.total_stores) * 100),
      trend: 4, color: "green"
    },
    {
      title: "Near Expiry",
      value: "50 SKUs",
      subtitle: "10,000 units at risk",
      progress: 60, trend: 8, color: "orange"
    },
    {
      title: "Stockout Rate",
      value: `${((stockStats.below_50 / stockStats.total_stores) * 100).toFixed(1)}%`,
      subtitle: `${stockStats.below_50} stores with low stock`,
      progress: 40, trend: -2, color: "red"
    },
    {
      title: "Pipeline Coverage",
      value: "74%",
      subtitle: "Active orders coverage",
      progress: 74, trend: 5, color: "purple"
    }
  ] : [
    { title: "Loading...", value: "...", subtitle: "", progress: 0, trend: 0, color: "blue" }
  ];

  const miniData = summary ? [
    { icon: <Landmark size={32} />, value: summary.totalDistricts, label: "Districts Covered", color: "green" },
    { icon: <Hospital size={32} />, value: summary.totalHmisFacilities?.toLocaleString(), label: "HMIS Facilities", color: "blue" },
    { icon: <Pill size={32} />, value: summary.totalCategories, label: "Product Category", color: "purple" },
    { icon: <BriefcaseMedical size={32} />, value: summary.totalProducts, label: "Total SKUs", color: "orange" }
  ] : [
    { icon: <Landmark size={32} />, value: "...", label: "Districts", color: "green" },
    { icon: <Hospital size={32} />, value: "...", label: "Facilities", color: "blue" },
    { icon: <Pill size={32} />, value: "...", label: "Categories", color: "purple" },
    { icon: <BriefcaseMedical size={32} />, value: "...", label: "SKUs", color: "orange" }
  ];

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />
      <FilterStrip />

      <div className="main">
        <DashboardSummary summaryData={summaryData} miniData={miniData} />

        <div className="viz-tabs-wrapper">
          <div className="viz-tab-bar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`vtab ${tab.cls}${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="vtab-dot"></span> {tab.label}
                {tab.badge && <span className="vtab-badge">{tab.badge}</span>}
              </button>
            ))}
          </div>

          {activeTab === 'tab1' && <TabStockAvailability />}
          {activeTab === 'tab2' && <TabNearExpiry />}
          {activeTab === 'tab3' && <TabStockout />}
          {activeTab === 'tab4' && <TabExpired />}
          {activeTab === 'tab5' && <TabPipeline />}
          {activeTab === 'tab6' && <Procurement />}
        </div>
      </div>

      <Footer />
    </div>
  );
}
